import { supabase } from '../../lib/supabase';
import { Task } from '../../types';
import { mapTaskFromDB } from '../mappers/entityMappers';

// ==================== CONSTANTES ====================

const TASK_SELECT_WITH_RELATIONS = `
    *,
    opportunities:opportunity_id (
        id, 
        title, 
        contacts (
            name, 
            phone, 
            email
        )
    ), 
    profiles!assigned_to (
        id, 
        full_name, 
        avatar_url
    )
`;

const TASK_SELECT_SIMPLE = `
    *, 
    opportunities:opportunity_id (id, title), 
    profiles!assigned_to (id, full_name, avatar_url)
`;

// ==================== TASK CRUD ====================

/**
 * Busca todas as tarefas com relacionamentos
 */
export async function getTasks(): Promise<Task[]> {
    try {
        const { data, error } = await supabase
            .from('tasks')
            .select(TASK_SELECT_WITH_RELATIONS)
            .order('due_date', { ascending: true });

        if (error) throw error;

        return (data || []).map(mapTaskFromDB);
    } catch (error) {
        console.warn('[TaskService] Fallback para query simples:', error);
        return getTasksSimple();
    }
}

/**
 * Fallback: busca tarefas sem joins complexos
 */
async function getTasksSimple(): Promise<Task[]> {
    const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .order('due_date', { ascending: true });

    if (error) {
        console.error('[TaskService] Erro no fallback:', error);
        return [];
    }

    return (data || []).map(mapTaskFromDB);
}

/**
 * Cria uma nova tarefa
 */
export async function createTask(
    task: Partial<Task> & { organizationId?: string }
): Promise<Task | null> {
    const dbTask = {
        title: task.title,
        description: task.description,
        due_date: task.dueDate,
        opportunity_id: task.leadId?.trim() || null,
        is_completed: task.isCompleted || false,
        assigned_to: task.assignedTo,
        organization_id: task.organizationId
    };

    const { data, error } = await supabase
        .from('tasks')
        .insert(dbTask)
        .select(TASK_SELECT_SIMPLE)
        .single();

    if (error) {
        console.error('[TaskService] Erro ao criar tarefa:', error);
        throw error;
    }

    return mapTaskFromDB(data);
}

/**
 * Atualiza uma tarefa existente
 */
export async function updateTask(id: string, updates: Partial<Task>): Promise<Task | null> {
    const dbUpdates: Record<string, unknown> = {};

    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
    if (updates.isCompleted !== undefined) dbUpdates.is_completed = updates.isCompleted;
    if (updates.leadId !== undefined) {
        dbUpdates.opportunity_id = updates.leadId?.trim() || null;
    }

    const { data, error } = await supabase
        .from('tasks')
        .update(dbUpdates)
        .eq('id', id)
        .select(TASK_SELECT_SIMPLE)
        .single();

    if (error) {
        console.error('[TaskService] Erro ao atualizar tarefa:', error);
        throw error;
    }

    return mapTaskFromDB(data);
}

/**
 * Exclui uma tarefa
 */
export async function deleteTask(id: string): Promise<boolean> {
    const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('[TaskService] Erro ao excluir tarefa:', error);
        return false;
    }

    return true;
}

// ==================== SELECTS (DROPDOWNS) ====================

interface SelectOption {
    value: string;
    label: string;
    /** UUID do responsável da negociação (`opportunities.assigned_to`), se houver */
    assignedToId?: string | null;
}

/**
 * Busca negociações para dropdown
 */
export async function getLeadsSelect(organizationId?: string): Promise<SelectOption[]> {
    try {
        let query = supabase
            .from('opportunities')
            .select('id, title, assigned_to')
            .not('title', 'is', null)
            .order('title', { ascending: true })
            .limit(100);

        if (organizationId) {
            query = query.eq('organization_id', organizationId);
        }

        const { data, error } = await query;

        if (error) throw error;

        return (data || [])
            .filter(o => o.title?.trim().length >= 2)
            .map(o => ({
                value: o.id,
                label: o.title,
                assignedToId: o.assigned_to ?? null,
            }));
    } catch (error) {
        console.error('[TaskService] Erro ao buscar leads:', error);
        return [];
    }
}

/**
 * Busca usuários da organização para dropdown
 */
export async function getUsersSelect(organizationId?: string): Promise<SelectOption[]> {
    if (!organizationId) return [];

    try {
        const { data: members, error: membersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', organizationId);

        if (membersError) throw membersError;

        const memberIds = (members || []).map(m => m.user_id);
        if (memberIds.length === 0) return [];

        const { data, error } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', memberIds)
            .order('full_name', { ascending: true });

        if (error) throw error;

        return (data || []).map(u => ({
            value: u.id,
            label: u.full_name || 'Usuário sem nome'
        }));
    } catch (error) {
        console.error('[TaskService] Erro ao buscar usuários:', error);
        return [];
    }
}
